import {
  BellOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MoonOutlined,
  SearchOutlined,
  SunOutlined,
  UserOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import {
  Avatar,
  Breadcrumb,
  Button,
  Dropdown,
  Layout,
  Menu,
  Space,
  theme,
} from "antd";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useEnumsQuery, usePermissionsQuery } from "@/hooks/useAdminQueries";

import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

import { adminApi } from "@/api/client";
import { CommandPalette } from "@/components/CommandPalette";
import {
  MENU_TREE,
  flattenMenuLeaves,
  type BuiltMenuNode,
} from "@/routes/menuMeta";
import { useAuthStore } from "@/stores/authStore";
import { useThemeStore } from "@/stores/themeStore";
import { hasPermission } from "@/utils/permissions";

const { Header, Sider, Content } = Layout;

function buildMenuItems(
  perms: string[],
  nodes: BuiltMenuNode[],
  t: (k: string) => string
): MenuProps["items"] {
  const mapLeaf = (leaf: { key: string; path: string; labelKey: string }) => ({
    key: leaf.path,
    label: t(leaf.labelKey),
  });

  const out: MenuProps["items"] = [];
  for (const n of nodes) {
    if ("path" in n) {
      if (!hasPermission(perms, n.requiredPermissions)) continue;
      out.push(mapLeaf(n));
    } else {
      const kids = n.children
        .filter((c) => hasPermission(perms, c.requiredPermissions))
        .map((c) => mapLeaf(c));
      if (!kids.length) continue;
      out.push({
        key: `sub-${n.key}`,
        label: t(n.labelKey),
        type: "submenu",
        children: kids,
      });
    }
  }
  return out;
}

export function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [collapsed, setCollapsed] = useState(() =>
    window.matchMedia("(max-width: 1023px)").matches
  );

  useEffect(() => {
    const mqInner = window.matchMedia("(max-width: 1023px)");
    const onChange = () => setCollapsed(mqInner.matches);
    mqInner.addEventListener("change", onChange);
    return () => mqInner.removeEventListener("change", onChange);
  }, []);

  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const { data: permissions = [] } = usePermissionsQuery(true);
  /** 登录且通过 MFA 后全站预热枚举缓存（契约单源，见 ADM-PLAT-001） */
  useEnumsQuery(true);

  const menuItems = useMemo(
    () => buildMenuItems(permissions, MENU_TREE, t),
    [permissions, t]
  );

  const clearSession = useAuthStore((s) => s.clearSession);

  const onLogout = async () => {
    const tokenAccess = useAuthStore.getState().accessToken;
    if (tokenAccess) {
      try {
        await adminApi.POST("/admin/v1/auth/logout");
      } catch {
        // ignore
      }
    }
    clearSession();
    qc.removeQueries({ queryKey: ["admin"] });
    navigate("/login", { replace: true });
  };

  const mode = useThemeStore((s) => s.mode);
  const toggleTheme = useThemeStore((s) => s.toggle);

  const pathname = location.pathname;
  const crumbLeaf = flattenMenuLeaves().find(
    (l) =>
      pathname === l.path ||
      (l.path !== "/dashboard" && pathname.startsWith(`${l.path}/`))
  );

  const crumbLabelKey =
    crumbLeaf?.labelKey ??
    (pathname.includes("/users/") ? "menu.users" : pathname);

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        breakpoint="lg"
        collapsedWidth={64}
        collapsible
        collapsed={collapsed}
        onCollapse={(c) => setCollapsed(c)}
        trigger={null}
        style={{ borderRight: `1px solid ${token.colorSplit}` }}
      >
        <div
          style={{
            height: 48,
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "flex-start",
            paddingInline: collapsed ? 0 : 16,
            fontWeight: 600,
            letterSpacing: "0.02em",
          }}
        >
          {!collapsed ? (
            <Link
              to="/dashboard"
              style={{ color: "inherit", textDecoration: "none" }}
              aria-label={t("menu.dashboard")}
            >
              AIVPN Admin
            </Link>
          ) : (
            <span aria-hidden>AI</span>
          )}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems ?? []}
          onClick={({ key }) => navigate(String(key))}
        />
      </Sider>
      <Layout>
        <Header
          style={{
            display: "flex",
            alignItems: "center",
            paddingInline: 16,
            background: token.colorBgContainer,
            borderBottom: `1px solid ${token.colorSplit}`,
            gap: 8,
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? "expand sidebar" : "collapse sidebar"}
          />
          <Breadcrumb
            style={{ flex: 1, minWidth: 0 }}
            items={[
              { title: <Link to="/dashboard">{t("menu.dashboard")}</Link> },
              { title: t(crumbLabelKey) },
            ]}
          />
          <Space size="middle">
            <Button
              type="text"
              icon={<SearchOutlined />}
              aria-label={t("common.search")}
              onClick={() => setPaletteOpen(true)}
            />
            <Button
              type="text"
              icon={<BellOutlined />}
              aria-label="notifications placeholder"
            />
            <Button
              type="text"
              icon={mode === "dark" ? <SunOutlined /> : <MoonOutlined />}
              onClick={toggleTheme}
              aria-label={`theme ${mode}`}
            />
            <Dropdown
              menu={{
                items: [
                  {
                    key: "logout",
                    icon: <LogoutOutlined />,
                    label: t("common.logout"),
                    onClick: onLogout,
                  },
                ],
              }}
            >
              <Avatar icon={<UserOutlined />} aria-label="operator menu" />
            </Dropdown>
          </Space>
        </Header>
        <Content style={{ margin: 16 }}>
          <div className="fade-page">
            <Outlet />
          </div>
        </Content>
      </Layout>
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
      />
    </Layout>
  );
}
